import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Dumbbell, Target, Calendar, Zap, ChevronRight, ChevronLeft, Loader2, Check, Utensils, ClipboardList, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'build_muscle', label: 'Build Muscle', icon: Dumbbell, desc: 'Gain lean muscle mass' },
  { id: 'lose_fat', label: 'Lose Fat', icon: Zap, desc: 'Burn fat and get lean' },
  { id: 'strength', label: 'Get Stronger', icon: Target, desc: 'Increase raw strength' },
  { id: 'general_fitness', label: 'General Fitness', icon: Sparkles, desc: 'Overall health & wellness' },
];

const BODY_PARTS = [
  { id: 'chest', label: 'Chest' }, { id: 'back', label: 'Back' },
  { id: 'legs', label: 'Legs' }, { id: 'shoulders', label: 'Shoulders' },
  { id: 'biceps', label: 'Biceps' }, { id: 'triceps', label: 'Triceps' },
  { id: 'core', label: 'Core' }, { id: 'glutes', label: 'Glutes' },
  { id: 'full body', label: 'Full Body' },
];

const EQUIPMENT = [
  { id: 'full_gym', label: 'Full Gym' },
  { id: 'home_basic', label: 'Home (Dumbbells + Bench)' },
  { id: 'bodyweight', label: 'Bodyweight Only' },
];

const AnimatedExerciseThumb = ({ imageStart, imageEnd, name }) => {
  const { t } = useTranslation();
  const [showEnd, setShowEnd] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    const t = setInterval(() => setShowEnd(p => !p), 900);
    return () => clearInterval(t);
  }, [loaded]);
  if (!imageStart) return <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center"><Dumbbell size={20} className="text-gray-400" /></div>;
  return (
    <div className="relative w-full h-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
      <img src={imageStart} alt={name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" style={{ opacity: showEnd ? 0 : 1 }} onLoad={() => setLoaded(true)} loading="lazy" />
      {imageEnd && <img src={imageEnd} alt={name} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" style={{ opacity: showEnd ? 1 : 0 }} loading="lazy" />}
    </div>
  );
};

const WorkoutWizard = ({ token, onNavigate }) => {
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState('');
  const [focusAreas, setFocusAreas] = useState([]);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [equipment, setEquipment] = useState('full_gym');
  const [experience, setExperience] = useState('beginner');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [mealPlan, setMealPlan] = useState(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);
  const [showPlans, setShowPlans] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchSavedPlans(); }, []);

  const fetchSavedPlans = async () => {
    try {
      const res = await axios.get(`${API}/exercise/workout-wizard/plans`, { headers });
      setSavedPlans(res.data.plans || []);
    } catch (e) { console.error(e); }
  };

  const toggleFocus = (id) => {
    setFocusAreas(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  const generatePlan = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/exercise/workout-wizard`, {
        goal, focus_areas: focusAreas, days_per_week: daysPerWeek,
        equipment, experience
      }, { headers });
      setPlan(res.data.plan);
      setStep(5);
    } catch (e) {
      console.error(e);
      alert('Failed to generate plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateMealPlan = async () => {
    setMealLoading(true);
    try {
      const res = await axios.post(`${API}/exercise/wizard-meal-plan`, {
        goal, focus_areas: focusAreas, days_per_week: daysPerWeek
      }, { headers });
      setMealPlan(res.data.meal_plan);
    } catch (e) { console.error(e); }
    finally { setMealLoading(false); }
  };

  const saveAsCustomList = async (dayKey, dayData) => {
    try {
      const exercises = dayData.exercises.filter(e => e.exercise_id).map(e => ({
        exercise_id: e.exercise_id, sets: e.sets, reps: e.reps
      }));
      await axios.post(`${API}/exercise/custom-lists`, {
        name: `${plan?.plan_name || 'Workout'} - ${dayKey}`,
        description: `${dayData.focus} day from AI workout wizard`,
        exercises
      }, { headers });
      alert(`Saved "${dayKey}" as custom workout list!`);
    } catch (e) { console.error(e); }
  };

  const steps = ['Goal', 'Focus', 'Schedule', 'Equipment', 'Generating'];

  if (showPlans && savedPlans.length > 0) {
    return (
      <div className="max-w-4xl mx-auto pb-8" data-testid="wizard-saved-plans">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('workoutWiz.myPlans')}</h2>
          <button onClick={() => setShowPlans(false)} className="text-sm text-[#FF6600] font-semibold" data-testid="create-new-plan-btn">{t('workoutWiz.createNew')}</button>
        </div>
        <div className="space-y-4">
          {savedPlans.map(p => (
            <div key={p.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setPlan(p); setStep(5); setShowPlans(false); }} data-testid={`saved-plan-${p.id}`}>
              <h3 className="font-bold text-gray-900 dark:text-white">{p.plan_name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">{p.plan_description}</p>
              <div className="flex gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">
                <span>{p.days_per_week} days/week</span>
                <span>{p.goal}</span>
                <span>{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (step === 5 && plan) {
    return (
      <div className="max-w-4xl mx-auto pb-8" data-testid="wizard-result">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => { setStep(0); setPlan(null); setMealPlan(null); }} className="text-sm text-[#FF6600] font-semibold flex items-center gap-1" data-testid="wizard-back-btn">
            <ChevronLeft size={16} /> New Plan
          </button>
          {savedPlans.length > 0 && (
            <button onClick={() => setShowPlans(true)} className="text-sm text-gray-600 dark:text-gray-300 font-semibold" data-testid="view-saved-plans-btn">
              My Plans ({savedPlans.length})
            </button>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{plan.plan_name}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{plan.plan_description}</p>

          {plan.tips && (
            <div className="flex flex-wrap gap-2 mb-4">
              {plan.tips.map((tip, i) => (
                <span key={i} className="text-xs bg-orange-50 dark:bg-orange-900/20 text-[#FF6600] px-3 py-1 rounded-full">{tip}</span>
              ))}
            </div>
          )}
        </div>

        {Object.entries(plan.weekly_schedule || {}).map(([dayKey, dayData]) => (
          <div key={dayKey} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm mb-3 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">{dayKey}</h3>
                <p className="text-xs text-[#FF6600]">{dayData.focus}</p>
              </div>
              <button onClick={() => saveAsCustomList(dayKey, dayData)} className="text-xs bg-[#FF6600] text-white px-3 py-1.5 rounded-lg font-semibold" data-testid={`save-day-${dayKey}`}>
                Save as List
              </button>
            </div>
            <div className="divide-y divide-gray-50 dark:divide-gray-700">
              {dayData.exercises?.map((ex, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                    <AnimatedExerciseThumb imageStart={ex.image_start} imageEnd={ex.image_end} name={ex.name} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{ex.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {ex.sets} sets x {ex.reps} reps{ex.rest ? ` | Rest: ${ex.rest}` : ''}
                    </p>
                    {ex.notes && <p className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 mt-0.5">{ex.notes}</p>}
                  </div>
                  {ex.muscle_group && (
                    <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full whitespace-nowrap">{ex.muscle_group}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Meal Plan Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mt-4">
          {!mealPlan ? (
            <div className="text-center">
              <Utensils size={32} className="mx-auto text-[#FF6600] mb-3" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Want a Meal Plan to Match?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                We'll create a nutrition plan aligned with your {goal} workout goals
              </p>
              <button onClick={generateMealPlan} disabled={mealLoading}
                className="bg-[#FF6600] text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2 mx-auto"
                data-testid="generate-meal-plan-btn">
                {mealLoading ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Utensils size={16} /> Generate Meal Plan</>}
              </button>
            </div>
          ) : (
            <div data-testid="meal-plan-result">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">{mealPlan.meal_plan_name}</h3>
              <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-300 mb-4">
                <span>{mealPlan.daily_calories} cal/day</span>
                {mealPlan.macros && (
                  <span>P: {mealPlan.macros.protein_g}g | C: {mealPlan.macros.carbs_g}g | F: {mealPlan.macros.fat_g}g</span>
                )}
              </div>
              {['training_day', 'rest_day'].map(dayType => (
                <div key={dayType} className="mb-4">
                  <h4 className="font-semibold text-sm text-[#FF6600] mb-2">{dayType === 'training_day' ? 'Training Day' : 'Rest Day'}</h4>
                  <div className="space-y-2">
                    {Object.entries(mealPlan[dayType] || {}).map(([mealKey, meal]) => (
                      <div key={mealKey} className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{mealKey.replace('_', ' ')}</p>
                          <p className="text-sm text-gray-900 dark:text-white">{meal.meal}</p>
                        </div>
                        <span className="text-xs font-semibold text-[#FF6600]">{meal.calories} cal</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {mealPlan.tips && (
                <div className="flex flex-wrap gap-2">
                  {mealPlan.tips.map((tip, i) => (
                    <span key={i} className="text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-1 rounded-full">{tip}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Start Workout Button */}
        <button
          onClick={() => onNavigate && onNavigate('session-logger')}
          className="w-full mt-4 bg-[#FF6600] text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-[#E55C00] transition-colors"
          data-testid="start-workout-btn">
          <Zap size={20} /> Start Workout
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-8" data-testid="workout-wizard">
      {savedPlans.length > 0 && step === 0 && (
        <button onClick={() => setShowPlans(true)} className="mb-4 text-sm text-[#FF6600] font-semibold flex items-center gap-1" data-testid="view-plans-link">
          <ClipboardList size={14} /> View My Plans ({savedPlans.length})
        </button>
      )}

      {/* Progress Bar */}
      <div className="flex gap-1 mb-6">
        {steps.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[#FF6600]' : 'bg-gray-200 dark:bg-gray-700'}`} />
        ))}
      </div>

      {/* Step 0: Goal */}
      {step === 0 && (
        <div className="space-y-4" data-testid="wizard-step-goal">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">What's your goal?</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Choose what you want to achieve</p>
          <div className="grid grid-cols-2 gap-3">
            {GOALS.map(g => (
              <button key={g.id} onClick={() => { setGoal(g.id); setStep(1); }}
                className={`p-4 rounded-xl border-2 text-left transition-all ${goal === g.id ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
                data-testid={`goal-${g.id}`}>
                <g.icon size={24} className={goal === g.id ? 'text-[#FF6600]' : 'text-gray-400'} />
                <p className="font-bold mt-2 text-gray-900 dark:text-white">{g.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{g.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: Focus Areas */}
      {step === 1 && (
        <div className="space-y-4" data-testid="wizard-step-focus">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Which areas do you want to focus on?</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Select one or more body parts</p>
          <div className="flex flex-wrap gap-2">
            {BODY_PARTS.map(bp => (
              <button key={bp.id} onClick={() => toggleFocus(bp.id)}
                className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all ${focusAreas.includes(bp.id) ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                data-testid={`focus-${bp.id}`}>
                {focusAreas.includes(bp.id) && <Check size={14} className="inline mr-1" />}
                {bp.label}
              </button>
            ))}
          </div>
          <div className="flex justify-between pt-4">
            <button onClick={() => setStep(0)} className="text-gray-600 dark:text-gray-300 font-semibold text-sm flex items-center gap-1"><ChevronLeft size={16} /> Back</button>
            <button onClick={() => focusAreas.length > 0 && setStep(2)} disabled={focusAreas.length === 0}
              className="bg-[#FF6600] text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-40 flex items-center gap-1" data-testid="next-step-btn">
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Days Per Week */}
      {step === 2 && (
        <div className="space-y-4" data-testid="wizard-step-schedule">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">How many days can you train?</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">We'll optimize your split accordingly</p>
          <div className="flex gap-3 justify-center py-4">
            {[2, 3, 4, 5, 6].map(d => (
              <button key={d} onClick={() => setDaysPerWeek(d)}
                className={`w-14 h-14 rounded-xl font-bold text-lg transition-all ${daysPerWeek === d ? 'bg-[#FF6600] text-white scale-110' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                data-testid={`days-${d}`}>
                {d}
              </button>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">{daysPerWeek} days per week</p>

          <div className="pt-2">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-2">Experience Level</h3>
            <div className="flex gap-2">
              {['beginner', 'intermediate', 'advanced'].map(lvl => (
                <button key={lvl} onClick={() => setExperience(lvl)}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm capitalize transition-all ${experience === lvl ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  data-testid={`exp-${lvl}`}>
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <button onClick={() => setStep(1)} className="text-gray-600 dark:text-gray-300 font-semibold text-sm flex items-center gap-1"><ChevronLeft size={16} /> Back</button>
            <button onClick={() => setStep(3)} className="bg-[#FF6600] text-white px-6 py-2.5 rounded-lg font-semibold flex items-center gap-1" data-testid="next-step-btn">
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Equipment */}
      {step === 3 && (
        <div className="space-y-4" data-testid="wizard-step-equipment">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">What equipment do you have?</h2>
          <div className="space-y-3">
            {EQUIPMENT.map(eq => (
              <button key={eq.id} onClick={() => setEquipment(eq.id)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${equipment === eq.id ? 'border-[#FF6600] bg-orange-50 dark:bg-orange-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}
                data-testid={`equip-${eq.id}`}>
                <p className="font-bold text-gray-900 dark:text-white">{eq.label}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-between pt-4">
            <button onClick={() => setStep(2)} className="text-gray-600 dark:text-gray-300 font-semibold text-sm flex items-center gap-1"><ChevronLeft size={16} /> Back</button>
            <button onClick={generatePlan} disabled={loading}
              className="bg-[#FF6600] text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="generate-plan-btn">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Sparkles size={16} /> Generate My Plan</>}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Loading */}
      {step === 4 && (
        <div className="text-center py-16" data-testid="wizard-loading">
          <Loader2 size={48} className="animate-spin text-[#FF6600] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Building Your Plan...</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">AI is crafting a personalized workout just for you</p>
        </div>
      )}
    </div>
  );
};

export default WorkoutWizard;
